import { ListTree } from "lucide-react";
import { OutsourceTag } from "../components/OutsourceTag";
import type { OutsourceStatus, QuoteSection } from "../api/types";

// 報價分類導覽(業主的用語)。七個分類加起來可能上百行,沒有導覽會迷路。
// 這裡只負責顯示與回報點擊,實際捲動由編輯器決定。
//
// 這一段只在報價單編輯頁出現:誰渲染它由 Sidebar 決定,沒有分類就整段不渲染。

/** 導覽項目的識別碼。新建但尚未存檔的分類沒有數字 id,改用暫時字串。 */
export type SectionNavId = number | string;

export interface SectionNavItem {
  id: SectionNavId;
  /** 顯示用序號,如「一」;可空。 */
  code: string;
  title: string;
  /** 發包狀態。側邊欄只讀,要改在章節卡片上改。 */
  outsourceStatus?: OutsourceStatus;
}

export interface SectionNavProps {
  sections: SectionNavItem[];
  onJump: (id: SectionNavId) => void;
  /** 目前所在分類,會標成選取狀態。 */
  activeId?: SectionNavId | null;
  className?: string;
}

/** 把報價單章節轉成導覽項目;尚未存檔的分類用序位當暫時識別碼。 */
export function toSectionNavItems(sections: QuoteSection[]): SectionNavItem[] {
  return sections.map((section, index) => ({
    id: section.id ?? `new-${index}`,
    code: section.code,
    title: section.title,
    outsourceStatus: section.outsource_status,
  }));
}

export function SectionNav({ sections, onJump, activeId = null, className = "" }: SectionNavProps) {
  return (
    <nav aria-label="報價分類" className={`flex min-h-0 flex-col gap-1 ${className}`}>
      <p className="m-0 flex items-center gap-2 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-3)]">
        <ListTree aria-hidden="true" size={14} />
        報價分類
      </p>
      {sections.length === 0 ? (
        <p className="m-0 px-3 py-1 text-sm text-[var(--text-3)]">尚未建立分類</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-0.5 overflow-y-auto p-0">
          {sections.map((section) => {
            const active = activeId != null && activeId === section.id;
            return (
              <li key={String(section.id)}>
                <button
                  type="button"
                  onClick={() => onJump(section.id)}
                  aria-current={active ? "true" : undefined}
                  className={
                    "flex w-full items-baseline gap-2 rounded-[var(--radius)] px-3 py-1.5 text-left text-sm " +
                    (active
                      ? "bg-[var(--accent-soft)] font-medium text-[var(--accent-hover)]"
                      : "text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]")
                  }
                >
                  {section.code ? (
                    <span className="w-4 shrink-0 text-xs text-[var(--text-3)]">{section.code}</span>
                  ) : null}
                  <span className="min-w-0 flex-1 break-words">{section.title || "(未命名分類)"}</span>
                  {/* 發包狀態只在這裡顯示,不接受點擊 —— 要改狀態在章節卡片上改。 */}
                  <OutsourceTag status={section.outsourceStatus} className="shrink-0 self-center" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}
